import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  FolderIcon,
  HeartIcon,
  HomeIcon,
  UserCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const NAV_ITEMS = [
  { name: 'Home', href: '/', icon: HomeIcon },
  { name: 'Books', href: '/books', icon: FolderIcon },
  { name: 'Favorites', href: '/favorites', icon: HeartIcon },
  { name: 'Profile', href: '/settings', icon: UserCircleIcon },
];

const NavigationItems = ({
  isActive,
  onNavigate,
  compact = false,
}) => (
  <nav className={compact ? 'space-y-1.5' : 'space-y-2'}>
    {NAV_ITEMS.map((item) => {
      const active = isActive(item.href);
      const Icon = item.icon;

      return (
        <Link
          key={item.name}
          to={item.href}
          onClick={onNavigate}
          aria-current={active ? 'page' : undefined}
          className={`group flex items-center rounded-xl font-medium transition-[background-color,color,transform] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
            compact ? 'px-3 py-2.5' : 'px-4 py-3'
          } ${
            active
              ? 'bg-primary-50 text-primary-700 font-semibold'
              : 'text-gray-600 hover:bg-gray-50 hover:text-primary-700'
          }`}
        >
          <Icon
            className={`w-5 h-5 mr-3 flex-none transition-colors ${
              active
                ? 'text-primary-600'
                : 'text-gray-400 group-hover:text-primary-500'
            }`}
          />
          <span>{item.name}</span>
          {active && (
            <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary-500" />
          )}
        </Link>
      );
    })}
  </nav>
);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const initials = useMemo(() => {
    if (!user) return '?';
    const first = String(user.first_name || '').trim();
    const last = String(user.last_name || '').trim();
    const value = `${first[0] || ''}${last[0] || ''}`;
    return (value || String(user.email || '?')[0]).toUpperCase();
  }, [user]);

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const handleLogout = async () => {
    setMobileMenuOpen(false);
    await logout();
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setMobileMenuOpen(false);
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileMenuOpen]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white/95 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="w-full px-3 sm:px-5 lg:px-6 flex items-center justify-between h-16">
          <div className="flex items-center min-w-0">
            <button
              type="button"
              onClick={() => setMobileMenuOpen((open) => !open)}
              className="lg:hidden p-2 rounded-lg text-gray-600 hover:text-primary-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              aria-label="Open navigation menu"
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation"
            >
              <Bars3Icon className="w-6 h-6" />
            </button>

            <Link
              to="/"
              className="hidden lg:flex items-center gap-2 min-w-0"
            >
              <img
                src="/assets/app-logo.png"
                alt="PawaLibrary"
                className="w-9 h-9 object-contain flex-none"
              />
              <span className="text-xl font-bold text-primary-700 truncate">
                PawaLibrary
              </span>
            </Link>
          </div>

          <Link to="/" className="lg:hidden flex items-center">
            <span className="text-lg font-bold text-primary-700">
              PawaLibrary
            </span>
          </Link>

          <div className="flex items-center gap-3">
            {user ? (
              <Link
                to="/settings"
                className="w-9 h-9 rounded-full bg-primary-600 text-white flex items-center justify-center hover:bg-primary-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                aria-label="Open profile settings"
              >
                <span className="text-sm font-semibold">{initials}</span>
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm font-medium text-gray-600 hover:text-primary-600"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="text-sm font-medium bg-primary-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        <aside className="hidden lg:flex lg:w-64 xl:w-72 flex-none bg-white border-r border-gray-200 sticky top-16 h-[calc(100vh-4rem)] flex-col">
          <div className="flex-1 overflow-y-auto px-3 xl:px-4 py-5">
            <NavigationItems isActive={isActive} compact />
          </div>

          {user && (
            <div className="border-t border-gray-100 p-3 xl:p-4">
              <button
                type="button"
                onClick={handleLogout}
                className="group w-full flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl border border-red-100 bg-red-50/70 text-sm font-semibold text-red-600 shadow-sm transition-[background-color,border-color,box-shadow,transform,color] duration-200 ease-out hover:bg-red-100 hover:border-red-200 hover:text-red-700 hover:shadow-md active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5 transition-transform duration-200 ease-out group-hover:translate-x-0.5" />
                Logout
              </button>
            </div>
          )}
        </aside>

        <div className="flex-1 min-w-0 flex flex-col">
          <main className="flex-1 w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-6 pb-24 lg:pb-6">
            {children}
          </main>

          <footer className="hidden lg:block bg-white border-t border-gray-200 py-4 text-center text-sm text-gray-500">
            &copy; {new Date().getFullYear()} PawaLibrary – All rights reserved.
          </footer>
        </div>
      </div>

      <div
        id="mobile-navigation"
        className={`fixed inset-0 z-50 lg:hidden transition-opacity duration-200 ${
          mobileMenuOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        aria-hidden={!mobileMenuOpen}
      >
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
          onClick={() => setMobileMenuOpen(false)}
        />
        <div
          className={`absolute left-0 top-0 h-full w-[82%] max-w-72 bg-white shadow-2xl transition-transform duration-300 ease-out transform flex flex-col ${
            mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2"
            >
              <img
                src="/assets/app-logo.png"
                alt=""
                className="w-8 h-8 object-contain"
              />
              <span className="text-lg font-bold text-primary-700">
                PawaLibrary
              </span>
            </Link>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="p-1.5 rounded-lg hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              aria-label="Close navigation menu"
            >
              <XMarkIcon className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <NavigationItems
              isActive={isActive}
              onNavigate={() => setMobileMenuOpen(false)}
            />
          </div>

          {user && (
            <div className="border-t border-gray-100 p-4">
              <button
                type="button"
                onClick={handleLogout}
                className="group flex items-center justify-center gap-2.5 w-full px-4 py-3 rounded-xl border border-red-100 text-red-600 bg-red-50/70 shadow-sm transition-[background-color,border-color,box-shadow,transform,color] duration-200 ease-out hover:bg-red-100 hover:border-red-200 hover:text-red-700 hover:shadow-md active:scale-[0.98] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5 transition-transform duration-200 ease-out group-hover:translate-x-0.5" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      <nav className="fixed bottom-0 left-0 right-0 z-30 lg:hidden bg-white/95 backdrop-blur-sm border-t border-gray-200 safe-area-bottom">
        <div className="flex justify-around items-stretch h-16 px-1">
          {NAV_ITEMS.map((item) => {
            const active = isActive(item.href);
            const Icon = item.icon;

            return (
              <Link
                key={item.name}
                to={item.href}
                aria-current={active ? 'page' : undefined}
                className={`relative flex flex-col items-center justify-center flex-1 py-1 px-0.5 transition-colors ${
                  active
                    ? 'text-primary-600'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {active && (
                  <span className="absolute top-0 w-8 h-0.5 rounded-full bg-primary-500" />
                )}
                <Icon className="w-6 h-6" />
                <span className="text-[10px] sm:text-xs mt-0.5 font-medium text-center leading-tight max-w-16 truncate">
                  {item.name}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}